import type { KeyboardEvent, MouseEvent, ReactNode } from 'react';
import { isPlainClick } from '@/components/plainClick';

/**
 * Shared card-row visual pattern (rcm-18, "see details on each claim easily").
 * Originally Queue.tsx's `Row()` for Denied/Underpaid; generalized so Appeals
 * sent and Closed render the same patient/amount/payer·codes/tag/meta shape
 * instead of a cramped `<table>`. Every consumer supplies plain strings/nodes;
 * this component only owns the markup and the `apl-qrow` classes (appeals.css
 * ~93-104).
 */
export interface CardRowTag {
  label: string;
  cls: string;
}

export function CardRow({
  href,
  selected,
  onSelect,
  name,
  amount,
  sub,
  tag,
  taglineText,
  metaLine,
  action,
  a11yLabel,
}: {
  /** When set, the row is an `<a>` (Queue's claim rows); otherwise a clickable div. */
  href?: string;
  selected?: boolean;
  onSelect?: () => void;
  name: string;
  amount: string;
  sub: string;
  tag: CardRowTag;
  /** The line beside the tag — reason / outcome / "what's happening". */
  taglineText?: string;
  /** The bottom meta line — deadline, sent date + channel, resolved date, etc. */
  metaLine?: string;
  /** A trailing action, e.g. the "Decide" button on Appeals sent rows. */
  action?: ReactNode;
  a11yLabel: string;
}) {
  const body = (
    <>
      <span className="apl-visually-hidden">{a11yLabel}</span>
      <div className="apl-qtop" aria-hidden="true">
        <span className="apl-qname">{name}</span>
        <span className="apl-qamt num">{amount}</span>
      </div>
      <div className="apl-qsub" aria-hidden="true">{sub}</div>
      <div className="apl-qtagline" aria-hidden="true">
        <span className={`apl-badge ${tag.cls}`}>{tag.label}</span>
        {taglineText ? <span>{taglineText}</span> : null}
      </div>
      {metaLine ? (
        <div className="apl-qdeadline" aria-hidden="true">
          {metaLine}
        </div>
      ) : null}
      {action ? (
        <div
          className="apl-qaction"
          aria-hidden="true"
          onClick={(e: MouseEvent) => { e.stopPropagation(); }}
        >
          {action}
        </div>
      ) : null}
    </>
  );

  if (href) {
    // When the caller supplies onSelect (Queue's SPA navigation), intercept the
    // click; otherwise (e.g. Closed's plain links to /claims/record/:id) let the
    // anchor navigate normally.
    return (
      <a
        href={href}
        className={`apl-qrow${selected ? ' apl-qsel' : ''}`}
        {...(selected ? { 'aria-current': 'true' as const } : {})}
        {...(onSelect
          ? {
              onClick: (e: MouseEvent) => {
                if (!isPlainClick(e)) return;
                e.preventDefault();
                onSelect();
              },
            }
          : {})}
      >
        {body}
      </a>
    );
  }

  return (
    <div
      className={`apl-qrow apl-qrow-btn${selected ? ' apl-qsel' : ''}`}
      role="button"
      tabIndex={0}
      {...(selected ? { 'aria-current': 'true' as const } : {})}
      onClick={() => { onSelect?.(); }}
      onKeyDown={(e: KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect?.();
        }
      }}
    >
      {body}
    </div>
  );
}
